import { Player } from "@videotranslator/player";
import "@videotranslator/player/css";
import { CircularProgress, Typography } from "@mui/material";
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import EventBus from "orion-components/SharedComponents/EventBus";
import StreamFailed from "../Wowza/utils/StreamFailed";

// Standard playback speeds supported by VideoTranslator player
const STANDARD_SPEED_RANGE = [-16, -8, -4, -2, -1, -0.5, 0.5, 1, 2, 4, 8, 16];

// Errors that have no impact on playback, e.g. in case of Cancel failed
// it's part of a retry loop that recovers without user intervention
const SUPPRESS_ERRORS = ["Cancel failed: 400"];

const VideoTranslatorPlayer = ({ instanceId, player, camera, streamUrl, selectedVideoMode }) => {
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const playerDiv = useRef();
	const playerRef = useRef();
	const selectedVideoModeRef = useRef(selectedVideoMode);
	const cursorRef = useRef({});
	const playerReadyRef = useRef(false);
	const pendingCommandsRef = useRef([]);
	const currentFrameRef = useRef(null);
	const frameAnchorInvalidRef = useRef(true);

	useEffect(() => {
		selectedVideoModeRef.current = selectedVideoMode;
	}, [selectedVideoMode]);

	useEffect(() => {
		EventBus.subscribe("Universal_PlaybackCommand", execPlaybackCommand);
		return () => {
			EventBus.unsubscribe("Universal_PlaybackCommand", execPlaybackCommand);
		};
	}, []);

	const publishCurrentRecordedTimestamp = () => {
		const currentTime = playerRef.current.getCurrentTime();
		const { startTime } = cursorRef.current || {};
		if (!startTime || currentTime === undefined) return;

		const timestamp = new Date(startTime.getTime() + currentTime * 1000);
		EventBus.publish("setCurrentRecordedTimestamp", { instanceId, time: timestamp });
	};

	const execPlaybackCommand = async cmd => {
		if (!playerReadyRef.current || !playerRef.current) {
			pendingCommandsRef.current.push(cmd);
			return;
		}
		// ignore if live
		if (selectedVideoModeRef.current === "live") {
			return;
		} else {
			const { instanceId: cmdInstanceId, command } = cmd;
			if (instanceId !== cmdInstanceId) return;
			switch (command) {
				// handle commands here
				case "play":
					// While playing, frames advance continuously; invalidate manual frame anchor.
					currentFrameRef.current = null;
					frameAnchorInvalidRef.current = true;
					playerRef.current.play();
					break;
				case "pause":
					playerRef.current.pause();
					// Do not trust getCurrentFrame() here; it may still reflect stale UI state.
					currentFrameRef.current = null;
					frameAnchorInvalidRef.current = true;
					break;
				case "setCursor":
					if (cmd.seeking) {
						const { time } = cmd;
						const { startTime } = cursorRef.current || {};
						if (!startTime) return;
						const newTimestamp = new Date(time);
						const secondsFromStart = (newTimestamp.getTime() - startTime.getTime()) / 1000;
						if (secondsFromStart > cursorRef.current.loadedDuration) return;
						// Reset frame tracking when scrubbing to a new position
						currentFrameRef.current = null;
						frameAnchorInvalidRef.current = true;
						playerRef.current.seek(secondsFromStart);
					} else {
						const startTime = new Date(cmd.time);
						const endTime = new Date(startTime.getTime() + cmd.duration * 1000);
						cursorRef.current = {
							startTime,
							endTime,
							duration: cmd.duration,
							loop: cmd.recordedMode === "clip"
						};
						// Reset frame tracking when loading a new time range
						currentFrameRef.current = null;
						frameAnchorInvalidRef.current = true;
						await playerRef.current.setMode("recorded", {
							timeRange: {
								context: "custom",
								startTime: startTime.toISOString(),
								endTime: endTime.toISOString()
							}
						});
						playerRef.current.play();
						EventBus.publish("RecordedBottomBar_startVideo", { instanceId, playing: true });
					}
					break;
				case "setSpeed":
					playerRef.current.setSpeed(cmd.speed);
					break;
				case "stepForward": {
					if (frameAnchorInvalidRef.current || currentFrameRef.current === null) {
						await playerRef.current.stepFrameForward();
						const playerFrameAfterStep = playerRef.current.getCurrentFrame();
						currentFrameRef.current = playerFrameAfterStep ?? null;
						frameAnchorInvalidRef.current = false;
					} else {
						const targetFrameForward = currentFrameRef.current + 1;

						await playerRef.current.seekToFrame(targetFrameForward);
						currentFrameRef.current = targetFrameForward;
					}

					publishCurrentRecordedTimestamp();
					break;
				}
				case "stepBackward": {
					if (frameAnchorInvalidRef.current || currentFrameRef.current === null) {
						await playerRef.current.stepFrameBackward();
						const playerFrameAfterStep = playerRef.current.getCurrentFrame();
						currentFrameRef.current = playerFrameAfterStep ?? null;
						frameAnchorInvalidRef.current = false;
					} else {
						const targetFrameBackward = Math.max(0, currentFrameRef.current - 1);

						await playerRef.current.seekToFrame(targetFrameBackward);
						currentFrameRef.current = targetFrameBackward;
					}

					publishCurrentRecordedTimestamp();
					break;
				}
			}
		}
	};

	useEffect(() => {
		if (playerRef.current) {
			// setMode("live") explicit on selectedVideoMode change, update to mode prop not sufficient
			// recorded handled by event bus in execPlaybackCommand
			if (selectedVideoMode === "live") {
				playerRef.current.setMode("live");
			}
		}
	}, [selectedVideoMode]);

	const handleStatusChange = status => {
		switch (status) {
			case "playing":
				setLoading(false);
				break;
			case "loading":
				setLoading(true);
				break;
			case "ended":
				if (selectedVideoModeRef.current === "recorded" && cursorRef.current.loop === true) {
					EventBus.publish("RecordedBottomBar_startVideo", { instanceId, playing: true });
					playerRef.current.seek(0);
					playerRef.current.play();
				}
			default:
				console.debug(`Player status changed to ${status} for ${camera.name}:`);
				break;
		}
	};

	const handleError = err => {
		const message = (err && (err.message || err.toString())) || "";
		const shouldSuppress = SUPPRESS_ERRORS.some(s => message.includes(s));

		if (!shouldSuppress) {
			setLoading(false);
			setError(true);
		}
		console.error("Native Player Error:", err);
	};

	const handleTimeUpdate = time => {
		if (selectedVideoModeRef.current === "live") return;
		// time is seconds since start time
		const { startTime, endTime, duration } = cursorRef.current || {};
		if (!startTime || !endTime) return;
		const currentTimestamp = new Date(startTime.getTime() + time * 1000);
		EventBus.publish("setCurrentRecordedTimestamp", { instanceId, time: currentTimestamp });
	};

	const handleDurationChange = duration => {
		if (selectedVideoModeRef.current === "recorded") {
			cursorRef.current.loadedDuration = duration;
			EventBus.publish("loadedDurationChange", { instanceId, duration });
		}
	};

	const handlePlayerReady = () => {
		playerReadyRef.current = true;

		// Publish player capabilities when ready (only in recorded mode)
		if (selectedVideoModeRef.current !== "live") {
			console.debug(
				`[VideoTranslatorPlayer][${instanceId}] Publishing capabilities (handlePlayerReady in recorded mode):`,
				STANDARD_SPEED_RANGE
			);
			EventBus.publish("VideoPlayer_CapabilitiesReady", {
				instanceId,
				supportedSpeeds: STANDARD_SPEED_RANGE
			});
		}

		if (pendingCommandsRef.current.length > 0) {
			pendingCommandsRef.current.forEach(cmd => execPlaybackCommand(cmd));
			pendingCommandsRef.current = [];
		}
	};

	const handleRetryStream = () => {
		setError(false);
		setLoading(true);
		playerRef.current.reconnect();
	};

	return (
		<div style={{ position: "relative" }} ref={playerDiv}>
			{streamUrl ? (
				<Player
					ref={player => {
						playerRef.current = player;
					}}
					streamId={camera.id}
					name={camera.entityData.properties.name || camera.id}
					rtspUrl={streamUrl}
					streamSetup={player.streamSetup || "auto"}
					ilbServiceUrl={player.ilbUrl}
					mediaMtxWebrtcUrl={player.mtxWebrtcUrl}
					mediaMtxApiUrl={player.mtxApiUrl}
					hlsServiceUrl={player.hlsUrl}
					theme={"default"}
					mode={selectedVideoModeRef.current}
					autoplay={selectedVideoModeRef.current === "live" ? true : false}
					muted={true}
					controls={false}
					onReady={handlePlayerReady}
					onTimeUpdate={handleTimeUpdate}
					onStatusChange={handleStatusChange}
					onError={handleError}
					onDurationChange={handleDurationChange}
					hideTakeScreenshot={true}
					hideDownloadClip={true}
					hideStreamInfo={true}
					hideShowHideControls={true}
					hideReconnect={true}
					hideLoadingOverlay={true}
					hidePlayButton={true}
					hideErrorOverlay={true}
					roundedCorners={false}
				/>
			) : null}
			{loading && !error && (
				<div
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background: "rgba(0,0,0,0.3)",
						zIndex: 2
					}}
				>
					<CircularProgress />
				</div>
			)}
			{error && (
				<div
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background: "rgba(0,0,0,0.6)",
						zIndex: 2001
					}}
				>
					<StreamFailed retryStream={handleRetryStream} />
				</div>
			)}
		</div>
	);
};

VideoTranslatorPlayer.propTypes = {
	player: PropTypes.object.isRequired,
	camera: PropTypes.object.isRequired,
	streamUrl: PropTypes.string.isRequired
};

export default VideoTranslatorPlayer;
